import PropTypes from "prop-types";
import { Rect } from "react-konva";
import "../styles.css";
import { setTextBoxDeselecting } from "../text/TextBoxWithButtons";
import { isPrimaryMouseButton } from "../../utils/pointerButtonUtils";

const Background = ({
  setSelectedTextId,
  transformerRef,
  setSelectedArrow,
  setSelectedLine = null, // Add setSelectedLine for unified line/arrow elements
  setSelectedDoubleSidedArrow = null, // Add setSelectedDoubleSidedArrow for unified elements
  setSelectedText,
  setSelectedObject,
  selectedObject = null, // CRITICAL: Current selected object to check if element is selected
  selectedObjects = [],
  selectedObjectsRef = null, // Ref for live selected objects
  setShowTextBox,
  setSelectedImage,
  setAction,
  ACTIONS,
  action,
  isDrawingArrow,
  isDrawingDoubleArrow,
  isDrawingLine,
  setSelectedObjects,
  setTexts,
  setEditTextBoxContent,
  setShowEditTask,
  setShowEditNote,
  lockedAction,
  isCloneMode, // Don't clear selectedObject when clone mode is active
  justFinishedSelectionRef, // Ref to check if we just finished a selection box drag
  pendingSelectionRef = null, // Ref to clear pending selection when clearing selections
  pendingChangesRef = null, // Ref to check pending changes; only call endElementEditing when true so selection effect can clear on first click
  endElementEditing = () => { },
  isElementBeingEdited = false,
  cloneElementsAtPosition = null, // Function to clone elements at position
  cloneTemplates = [], // Templates for cloning (multi-element)
  cloneTemplate = null, // Single-element clone template
  stageRef = null, // Stage ref to get pointer position
  clickedStageRef = null, // Optional stage ref to pass to cloneElementsAtPosition (for off-canvas)
  setIsMultiSelectMode = null, // Function to disable multi-select mode
  isMultiSelectMode = false, // Multi-select mode flag
  isMultiSelectModeRef = null, // Ref with current isMultiSelectMode (avoids stale closure when Background receives click)
  getTopLayerImageAtClick = null, // (clientX, clientY) => imageObj | null — when multi-select on, top layer has pointer-events: none so Background gets the click
  onTopLayerImageClicked = null, // (imageObj) => void — select top-layer image in normal select mode
  onTopLayerImageClickedInMultiSelect = null, // (imageObj) => void — add top-layer image to selection instead of clearing
  recentDraggableElementMousedownRef = null, // Ref to track recent draggable element mousedown events
  setIsSelecting = null, // Function to reset selection box state
  setSelectionBox = null, // Function to clear selection box
  setSelectionBoxStage = null, // Function to clear selection box stage
  isCropModalOpen = false, // When crop modal is open, don't clear selection (modal interaction must not close it)
  isCropModalOpenRef = null, // Ref set synchronously on Crop click (fixes first-time close)
  justFinishedTransformRef = null, // When true, skip clearing selection (click that fires after transform end)
  lastTransformStartTimeRef = null, // Time-based fallback: skip clear if transform started in last 250ms
  justFinishedDragRef = null, // When true, skip clearing selection (click that fires after drag end, e.g. after transform then drag)
  justFinishedShapeDrawRef = null, // After circle draw end, click can hit bg (outside stroke); skip clear
  lastDraggedImageRef = null, // { id, time } - skip clear if selectedObject is this image and time is recent
}) => {
  const blurCanvasNumericInputs = () => {
    if (typeof document === "undefined") return;
    const activeEl = document.activeElement;
    if (!activeEl || typeof activeEl.blur !== "function") return;
    const isZoomInput =
      typeof activeEl.classList?.contains === "function" &&
      activeEl.classList.contains("zoom-input");
    const isCanvasHeightInput = activeEl.id === "canvas-height-input";
    if (isZoomInput || isCanvasHeightInput) {
      activeEl.blur();
    }
  };

  return (
    <Rect
      x={0}
      y={0}
      height={window.innerHeight}
      width={window.innerWidth}
      fill="#ffffff"
      id="bg"
      perfectDrawEnabled={false}
      shadowForStrokeEnabled={false}
      listening={true}
      onMouseDown={(e) => {
        blurCanvasNumericInputs();
        // When crop modal is open, ignore all Background pointer events (resize/drag in modal must not hit canvas)
        if (isCropModalOpen || isCropModalOpenRef?.current) {
          e.cancelBubble = true;
          if (e.evt) {
            e.evt.preventDefault?.();
            e.evt.stopPropagation?.();
          }
          return;
        }
        // CRITICAL: In clone mode with lockedAction (drag-to-draw mode), don't create clone on click
        // The clone will be created when dragging starts (onPointerMove) or on pure click (onPointerUp)
        // This prevents creating a clone on click when the user intends to drag-to-draw
        // Also prevents duplicate clones: Background creates one, then onPointerUp creates another
        const hasCloneTemplates = cloneTemplates && cloneTemplates.length > 0;
        const hasCloneTemplate = cloneTemplate !== null;
        // Drag-to-draw mode is only when lockedAction is a drawing action (not SELECT)
        // SELECT is used for multi-clone mode which is click-to-clone, not drag-to-draw
        const isDragToDrawMode =
          (isCloneMode || lockedAction) &&
          lockedAction &&
          lockedAction !== ACTIONS.SELECT;

        // Skip clone creation in drag-to-draw mode - let onPointerUp handle it
        if (isDragToDrawMode) {
          return;
        }

        // Handle clone mode in onMouseDown (fires before onClick)
        // Check for both single-element (cloneTemplate) and multi-element (cloneTemplates) clone mode
        // Handle for both off-canvas (clickedStageRef provided) and main canvas (clickedStageRef not provided)
        const shouldClone =
          (isCloneMode || lockedAction) &&
          cloneElementsAtPosition &&
          (hasCloneTemplates || hasCloneTemplate) &&
          e.evt?.button === 0;

        if (shouldClone) {
          const stage = stageRef?.current;
          if (stage) {
            stage.setPointersPositions(e.evt);
            const pointerPos = stage.getPointerPosition();
            if (pointerPos) {
              // Get client coordinates for determining which canvas to add elements to
              const clientX = e.evt?.clientX;
              const clientY = e.evt?.clientY;
              // Clone at clicked position, passing stage ref and client coordinates
              // Use clickedStageRef if provided (for off-canvas), otherwise use stageRef (for main canvas)
              const stageToPass = clickedStageRef || stageRef;
              cloneElementsAtPosition(
                pointerPos.x,
                pointerPos.y,
                stageToPass,
                clientX,
                clientY
              );
              e.cancelBubble = true;
              if (e.evt) {
                e.evt.preventDefault();
                e.evt.stopPropagation();
              }
              return;
            }
          }
        }
      }}
      onClick={(e) => {
        // Identify which Background component this is (main vs off-canvas)
        const isOffCanvas = clickedStageRef != null;
        console.log(`🟡 [BACKGROUND${isOffCanvas ? '-OFFCANVAS' : '-MAIN'}] onClick handler called`, {
          targetId: e.target?.attrs?.id,
          targetType: e.target?.getClassName?.(),
          currentTargetId: e.currentTarget?.attrs?.id,
          currentTargetType: e.currentTarget?.getClassName?.(),
        });
        
        blurCanvasNumericInputs();
        if (!isPrimaryMouseButton(e.evt)) {
          return;
        }
        // When crop modal is open, never clear selection (modal resize/drag can fire events that hit Background)
        if (isCropModalOpen || isCropModalOpenRef?.current) {
          e.cancelBubble = true;
          if (e.evt) {
            e.evt.preventDefault?.();
            e.evt.stopPropagation?.();
          }
          return;
        }

        // Use the same background click detection logic as clickHandler.js for consistency
        // Check if clicking on background: no target, stage, background Rect (id="bg"), or Transformer (selection box)
        const targetId = e.target?.attrs?.id;
        const isBackgroundClick =
          !targetId ||
          e.target.getClassName() === "Stage" ||
          targetId === "bg" ||
          e.target.getClassName() === "Transformer";

        if (!isBackgroundClick) {
          // Click is on an element, don't handle in Background
          return;
        }

        // CRITICAL: If we just finished a transform (resize/rotate) or a drag, skip clearing — do this first, before any state changes
        const justFinishedTransform = justFinishedTransformRef?.current;
        const justFinishedDrag = justFinishedDragRef?.current;
        const rawShapeDrawGuard = justFinishedShapeDrawRef?.current;
        const justFinishedShapeDraw =
          typeof rawShapeDrawGuard === "number"
            ? Date.now() - rawShapeDrawGuard < 60
            : Boolean(rawShapeDrawGuard);
        const transformStartedRecently = lastTransformStartTimeRef?.current && (Date.now() - lastTransformStartTimeRef.current < 300);
        if (justFinishedTransform || justFinishedDrag || justFinishedShapeDraw || transformStartedRecently) {
          if (justFinishedTransformRef) justFinishedTransformRef.current = false;
          if (justFinishedDragRef) justFinishedDragRef.current = false;
          if (justFinishedShapeDrawRef) justFinishedShapeDrawRef.current = 0;
          e.cancelBubble = true;
          if (e.evt) {
            e.evt.preventDefault?.();
            e.evt.stopPropagation?.();
          }
          return;
        }

        // CRITICAL FIX: Always clear recentDraggableElementMousedownRef on background click
        // This prevents the two-click deselection issue after drag-to-create
        if (recentDraggableElementMousedownRef) {
          const recentMousedown = recentDraggableElementMousedownRef.current;

          if (recentMousedown.nodeId) {
            const ageMs = Date.now() - recentMousedown.timestamp;
            if (ageMs < 4000) {
              e.cancelBubble = true;
              if (e.evt) {
                e.evt.preventDefault?.();
                e.evt.stopPropagation?.();
              }
              return;
            }
            // Clear the ref immediately
            recentDraggableElementMousedownRef.current = { nodeId: null, timestamp: 0 };
            // Don't return early - continue with deselection logic
          }
        }

        setSelectedText(null);

        // CRITICAL: In clone mode with lockedAction (drag-to-draw mode), don't create clone on click
        // The clone will be created when dragging starts (onPointerMove) or on pure click (onPointerUp)
        // This prevents creating a clone on click when the user intends to drag-to-draw
        const hasCloneTemplates = cloneTemplates && cloneTemplates.length > 0;
        const hasCloneTemplate = cloneTemplate !== null;
        // Drag-to-draw mode is only when lockedAction is a drawing action (not SELECT)
        // SELECT is used for multi-clone mode which is click-to-clone, not drag-to-draw
        const isDragToDrawMode =
          (isCloneMode || lockedAction) &&
          lockedAction &&
          lockedAction !== ACTIONS.SELECT;


        if (isDragToDrawMode) {
          return;
        }

        // Click-to-clone is handled on Background onMouseDown only. Doing it again here on onClick
        // duplicated every element once per click (mousedown + click both fired).

        // If lockedAction is set (but not clone mode), don't interfere
        // BUT: If lockedAction is SELECT (multi-clone mode), we already handled it above, so don't return early
        if (lockedAction && lockedAction !== ACTIONS.SELECT) {
          e.cancelBubble = true;
          if (e.evt) {
            e.evt.preventDefault();
            e.evt.stopPropagation();
          }
          return;
        }

        // While a drawing tool is active, pointer handlers own create/select.
        // Do not run background clear logic on the synthetic click after pointer-up.
        if (action && action !== ACTIONS.SELECT && action !== ACTIONS.PAN) {
          e.cancelBubble = true;
          if (e.evt) {
            e.evt.preventDefault();
            e.evt.stopPropagation();
          }
          return;
        }

        // Stop event propagation to ensure this handler executes
        e.cancelBubble = true;
        if (e.evt) {
          e.evt.stopPropagation();
        }

        // Top-layer images can route clicks through Background (pointer-events toggling, overlays).
        // If this click is over a top-layer image, select it and do not clear.
        const multiSelectActive = isMultiSelectModeRef?.current ?? isMultiSelectMode;
        if (
          getTopLayerImageAtClick &&
          (onTopLayerImageClickedInMultiSelect || onTopLayerImageClicked) &&
          e.evt
        ) {
          const topLayerImage = getTopLayerImageAtClick(e.evt.clientX, e.evt.clientY);
          if (topLayerImage) {
            if (multiSelectActive && onTopLayerImageClickedInMultiSelect) {
              onTopLayerImageClickedInMultiSelect(topLayerImage);
            } else if (onTopLayerImageClicked) {
              onTopLayerImageClicked(topLayerImage);
            }
            e.cancelBubble = true;
            if (e.evt) {
              e.evt.stopPropagation?.();
              e.evt.preventDefault?.();
            }
            return;
          }
        }

        // Determine if we're in clone mode (check multiple indicators since isCloneMode might be undefined)
        // Clone mode is active if: isCloneMode is true, lockedAction is set, or we have clone templates
        const isInCloneMode =
          isCloneMode || lockedAction || hasCloneTemplates || hasCloneTemplate;

        // Do NOT call endElementEditing here. Clear selection only; the selection effect in MyCanvas
        // runs when selectedObject becomes null and then calls endElementEditing (if pending changes)
        // or clears isElementBeingEdited. That way first click always triggers the effect.

        // Don't clear any selections if clone mode is active (keeps editor open)
        // Also don't clear if lockedAction is set (clone mode uses lockedAction)
        // If we just finished a selection box drag, the "click" is the release — do NOT clear
        // or we would wipe out the selection we just applied.
        // When crop modal is open (state or ref), don't clear. Ref fixes first-time close.
        // Top-layer image clicks are handled above (add to selection and return); genuine background click should clear.
        // If selected object is an image that was just dragged (transform then drag → drag end), do NOT clear.
        const justFinishedSelection =
          justFinishedSelectionRef && justFinishedSelectionRef.current;
        const rawShapeDrawJustFinished = justFinishedShapeDrawRef?.current;
        const shapeDrawJustFinished =
          typeof rawShapeDrawJustFinished === "number"
            ? Date.now() - rawShapeDrawJustFinished < 60
            : Boolean(rawShapeDrawJustFinished);
        const lastDragged = lastDraggedImageRef?.current;
        // Guard against the synthetic click that can fire immediately after a drag end:
        // only skip clear when that click is for the same image and within a short window.
        const isImageJustDragged =
          lastDragged?.id != null &&
          lastDragged?.time != null &&
          selectedObject?.type === "Image" &&
          selectedObject?.id === lastDragged.id &&
          Date.now() - lastDragged.time < 500;
        // Use the same logic as clickHandler.js for checking live selected objects
        const liveSelectedObjects = selectedObjectsRef?.current && Array.isArray(selectedObjectsRef.current)
          ? selectedObjectsRef.current
          : selectedObjects;
        const hasGroupSelection = (liveSelectedObjects?.length || 0) > 1;
        
        // DEBUG: Log selection state
        console.log("🟡 [BACKGROUND] Selection state check", {
          selectedObjectsLength: selectedObjects?.length || 0,
          selectedObjectsRefCurrent: selectedObjectsRef?.current,
          selectedObjectsRefLength: selectedObjectsRef?.current?.length || 0,
          liveSelectedObjectsLength: liveSelectedObjects?.length || 0,
          hasGroupSelection,
          targetId,
          targetType: e.target?.getClassName?.(),
        });
        
        const shouldClear =
          !isInCloneMode &&
          action === ACTIONS.SELECT &&
          (liveSelectedObjects?.length || 0) <= 1 &&
          !justFinishedSelection &&
          !shapeDrawJustFinished &&
          !isCropModalOpen &&
          !isCropModalOpenRef?.current &&
          !isImageJustDragged &&
          !hasGroupSelection; // Don't clear if we have group selection

        console.log(`🟡 [BACKGROUND${isOffCanvas ? '-OFFCANVAS' : '-MAIN'}] shouldClear check`, { 
          shouldClear, 
          hasGroupSelection,
          liveSelectedObjectsLength: liveSelectedObjects?.length || 0,
          action,
          isInCloneMode,
        });
        if (shouldClear) {
          console.log(`🟡 [BACKGROUND${isOffCanvas ? '-OFFCANVAS' : '-MAIN'}] Clearing selections in Background`);
          // CRITICAL FIX: Clear pendingSelection to prevent re-selection after clearing
          // This fixes the issue where selectedImage gets set back after we clear it
          if (pendingSelectionRef) {
            pendingSelectionRef.current = null;
          }

          // Reset justFinishedSelectionRef when clearing from background click
          // This ensures future interactions work correctly
          if (justFinishedSelectionRef) {
            justFinishedSelectionRef.current = false;
          }
          // CRITICAL FIX: Reset selection box state when clearing selections
          // This ensures the next selection box can be drawn properly
          if (setIsSelecting) {
            setIsSelecting(false);
          }
          if (setSelectionBox) {
            setSelectionBox(null);
          }
          if (setSelectionBoxStage) {
            setSelectionBoxStage(null);
          }
          transformerRef?.current?.nodes([]);
          setSelectedText(null);
          setTextBoxDeselecting(true); // <-- Set the flag before hiding the text box
          setShowTextBox(false);
          setSelectedTextId(null);
          setSelectedArrow(null);
          // Also clear line and double-sided arrow selections (for unified elements)
          if (setSelectedLine) setSelectedLine(null);
          if (setSelectedDoubleSidedArrow) setSelectedDoubleSidedArrow(null);
          setSelectedObject(null);
          setSelectedImage(null);
          setSelectedObjects([]);
          // CRITICAL: Also disable multi-select mode when clearing selections
          if (setIsMultiSelectMode) {
            setIsMultiSelectMode(false);
          } else {
          }
        } else {
        }

        // Clear text editing states
        if (setEditTextBoxContent) setEditTextBoxContent(false);
        if (setShowEditTask) setShowEditTask(false);
        if (setShowEditNote) setShowEditNote(false);

        // 🧼 Clear all selectedContent from text nodes
        setTexts((prevTexts) =>
          prevTexts.map((t) => ({
            ...t,
            selectedContent: null,
          }))
        );

        // Don't reset action if a tool is locked or if PAN is active
        if (
          !isDrawingArrow &&
          !isDrawingDoubleArrow &&
          !isDrawingLine &&
          !lockedAction &&
          action !== ACTIONS.PAN
        ) {
          setAction(ACTIONS.SELECT);
        }
      }}
    />
  );
};

// ✅ Define PropTypes for validation
Background.propTypes = {
  setSelectedTextId: PropTypes.func.isRequired, // Function to deselect text
  transformerRef: PropTypes.shape({
    current: PropTypes.object, // Transformer reference (Konva)
  }).isRequired,
  setSelectedArrow: PropTypes.func.isRequired, // Function to deselect arrow
  setSelectedText: PropTypes.func.isRequired,
  setSelectedLine: PropTypes.func, // Function to deselect line
  setSelectedDoubleSidedArrow: PropTypes.func, // Function to deselect double-sided arrow
  setSelectedObject: PropTypes.func.isRequired, // Function to set selected object
  selectedObjects: PropTypes.array, // Current multi-selection array
  selectedObjectsRef: PropTypes.shape({ current: PropTypes.array }), // Ref for live selected objects
  setShowTextBox: PropTypes.func.isRequired, // Function to show/hide text box
  setSelectedImage: PropTypes.func.isRequired, // Function to select image
  setAction: PropTypes.func.isRequired, // Function to set action
  ACTIONS: PropTypes.object.isRequired, // Action constants
  action: PropTypes.string.isRequired, // Current action
  isDrawingArrow: PropTypes.bool, // Whether arrow is being drawn
  isDrawingDoubleArrow: PropTypes.bool, // Whether double arrow is being drawn
  isDrawingLine: PropTypes.bool, // Whether line is being drawn
  setSelectedObjects: PropTypes.func.isRequired, // Function to set selected objects
  setTexts: PropTypes.func.isRequired, // Function to set texts
  setEditTextBoxContent: PropTypes.func, // Function to set edit text box content
  setShowEditTask: PropTypes.func, // Function to show edit task
  setShowEditNote: PropTypes.func, // Function to show edit note
  lockedAction: PropTypes.string, // Locked action (clone mode)
  isCloneMode: PropTypes.bool, // Clone mode flag
  justFinishedSelectionRef: PropTypes.shape({
    current: PropTypes.bool, // Ref to check if just finished selection
  }),
  pendingChangesRef: PropTypes.shape({
    current: PropTypes.bool, // Ref for pending changes; only end editing when true
  }),
  endElementEditing: PropTypes.func, // Function to end element editing
  isElementBeingEdited: PropTypes.bool, // Whether element is being edited
  cloneElementsAtPosition: PropTypes.func, // Function to clone elements at position
  cloneTemplates: PropTypes.array, // Templates for cloning
  cloneTemplate: PropTypes.object, // Single-element clone template
  stageRef: PropTypes.shape({
    current: PropTypes.object, // Stage reference
  }),
  clickedStageRef: PropTypes.shape({
    current: PropTypes.object, // Clicked stage reference
  }),
  setIsMultiSelectMode: PropTypes.func, // Function to set multi-select mode
  isMultiSelectMode: PropTypes.bool, // Multi-select mode flag
  isMultiSelectModeRef: PropTypes.shape({ current: PropTypes.bool }),
  getTopLayerImageAtClick: PropTypes.func,
  onTopLayerImageClicked: PropTypes.func,
  onTopLayerImageClickedInMultiSelect: PropTypes.func,
  recentDraggableElementMousedownRef: PropTypes.shape({
    current: PropTypes.object, // Ref to track recent draggable element mousedown events
  }),
  justFinishedShapeDrawRef: PropTypes.shape({ current: PropTypes.bool }),
};

export default Background;
